/**
 * arrange.ts — pure geometry utilities for aligning, distributing and tidying
 * nodes on the canvas.
 *
 * All functions operate on plain `{ id, x, y, width, height }` items where
 * (x, y) is the top-left corner, and return a `Map<id, { x, y }>` of new
 * top-left positions. They are framework-agnostic and side-effect free so they
 * can be unit-tested in a node environment and reused anywhere.
 */

export interface ArrangeItem {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export type AlignEdge = 'left' | 'right' | 'hcenter' | 'top' | 'bottom' | 'vcenter'
export type DistributeAxis = 'horizontal' | 'vertical'

type Move = { x: number; y: number }

/**
 * Align items to a shared edge or center.
 *
 * - left    → all share the minimum left x.
 * - right   → all share the maximum right edge (x = maxRight - width).
 * - hcenter → all centered on the average of horizontal centers
 *             (x = avgCenterX - width / 2).
 * - top / bottom / vcenter → analogous on the y axis.
 */
export function alignNodes(items: ArrangeItem[], edge: AlignEdge): Map<string, Move> {
  const moves = new Map<string, Move>()
  if (items.length === 0) return moves

  switch (edge) {
    case 'left': {
      const minLeft = Math.min(...items.map((i) => i.x))
      for (const it of items) moves.set(it.id, { x: minLeft, y: it.y })
      break
    }
    case 'right': {
      const maxRight = Math.max(...items.map((i) => i.x + i.width))
      for (const it of items) moves.set(it.id, { x: maxRight - it.width, y: it.y })
      break
    }
    case 'hcenter': {
      const avgCenter =
        items.reduce((sum, i) => sum + (i.x + i.width / 2), 0) / items.length
      for (const it of items) moves.set(it.id, { x: avgCenter - it.width / 2, y: it.y })
      break
    }
    case 'top': {
      const minTop = Math.min(...items.map((i) => i.y))
      for (const it of items) moves.set(it.id, { x: it.x, y: minTop })
      break
    }
    case 'bottom': {
      const maxBottom = Math.max(...items.map((i) => i.y + i.height))
      for (const it of items) moves.set(it.id, { x: it.x, y: maxBottom - it.height })
      break
    }
    case 'vcenter': {
      const avgCenter =
        items.reduce((sum, i) => sum + (i.y + i.height / 2), 0) / items.length
      for (const it of items) moves.set(it.id, { x: it.x, y: avgCenter - it.height / 2 })
      break
    }
  }

  return moves
}

/**
 * Distribute items so the gaps between consecutive *centers* are equal across
 * the span. Requires at least 3 items (otherwise there is nothing to space).
 *
 * The two extreme items (smallest and largest center on the axis) are kept
 * fixed; inner items are evenly spaced by center along the span.
 */
export function distributeNodes(
  items: ArrangeItem[],
  axis: DistributeAxis,
): Map<string, Move> {
  const moves = new Map<string, Move>()
  if (items.length < 3) return moves

  const horizontal = axis === 'horizontal'
  const centerOf = (i: ArrangeItem) =>
    horizontal ? i.x + i.width / 2 : i.y + i.height / 2

  const sorted = [...items].sort((a, b) => centerOf(a) - centerOf(b))

  const firstCenter = centerOf(sorted[0])
  const lastCenter = centerOf(sorted[sorted.length - 1])
  const step = (lastCenter - firstCenter) / (sorted.length - 1)

  sorted.forEach((it, idx) => {
    const targetCenter = firstCenter + step * idx
    if (horizontal) {
      moves.set(it.id, { x: targetCenter - it.width / 2, y: it.y })
    } else {
      moves.set(it.id, { x: it.x, y: targetCenter - it.height / 2 })
    }
  })

  return moves
}

/**
 * Pack items into a tidy grid.
 *
 * - Columns = ceil(sqrt(n)).
 * - Cell size = (max width + gap) × (max height + gap) across all items.
 * - Items are placed in their current sort order (by y, then x), filling
 *   left-to-right, top-to-bottom.
 * - The grid is anchored at the bounding-box top-left of the original
 *   selection.
 */
export function tidyUpNodes(
  items: ArrangeItem[],
  opts?: { gap?: number },
): Map<string, Move> {
  const moves = new Map<string, Move>()
  if (items.length === 0) return moves

  const gap = opts?.gap ?? 32
  const cols = Math.ceil(Math.sqrt(items.length))

  const cellWidth = Math.max(...items.map((i) => i.width)) + gap
  const cellHeight = Math.max(...items.map((i) => i.height)) + gap

  const originX = Math.min(...items.map((i) => i.x))
  const originY = Math.min(...items.map((i) => i.y))

  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x)

  sorted.forEach((it, idx) => {
    const col = idx % cols
    const row = Math.floor(idx / cols)
    moves.set(it.id, {
      x: originX + col * cellWidth,
      y: originY + row * cellHeight,
    })
  })

  return moves
}
